import { makeAutoObservable, runInAction } from "mobx";
import agent from "../../api/agent";
import { ReportInput } from "../../models/reports";
import { PagingParams, Pagination } from "../../models/pagination";
import { ARReportFilter, DateRangeOption, ReportType } from "../../models/enums";
import { AccountsReceivableReportItemDto } from "../../models/accountsReceivable";

export default class AccountsReceivableReportStore {
    constructor() {
        makeAutoObservable(this);
    }

    loadingInitial = false;
    setLoadingInitial = (state: boolean) => {
        this.loadingInitial = state;
    };

    reportCriteria: ReportInput = new ReportInput({
        dateRange: DateRangeOption.AllTime,
        startDate: new Date(0),
        endDate: new Date(),
        reportType: ReportType.Summary,
        filter: "0",
        typeOfBalance: ARReportFilter.Open
    });

    accountsReceivableReportRegistry = new Map<string, AccountsReceivableReportItemDto>();

    resetARReportRegistry = () => {
        this.accountsReceivableReportRegistry.clear();
    };

    loadAccountsReceivableReport = async () => {
        this.loadingInitial = true;
        try {
            this.accountsReceivableReportRegistry.clear();
            const result = await agent.accountsReceivableReports.getAccountsReceivableReport(this.axiosParams);
            runInAction(() => {
                result.forEach((accountsReceivableReport) => {
                    this.setAccountsReceivableReport(accountsReceivableReport);
                });
            });
        } catch (error) {
            console.log(error);
        } finally {
            this.setLoadingInitial(false);
        }
    };

    private setAccountsReceivableReport = (accountsReceivableReport: AccountsReceivableReportItemDto) => {
        if(this.reportCriteria.reportType === ReportType.Detailed)
            this.accountsReceivableReportRegistry.set(accountsReceivableReport.ticketId ?? "", accountsReceivableReport);
        else 
            this.accountsReceivableReportRegistry.set(accountsReceivableReport.customerId ?? "", accountsReceivableReport);
    };

    get getAccountsReceivableReport() {
        return Array.from(this.accountsReceivableReportRegistry.values()).sort(
            (a, b) => (a.companyName ?? a.customerName)!.localeCompare((b.companyName ?? b.customerName)!)
        )
    }

    get groupedAccountsReceivableReport() {
        return Object.entries(
            this.getAccountsReceivableReport.reduce((groups, ac) => {

                const name = `${ac.companyName 
                                ? ac.companyName
                                : ac.customerName 
                                    ? ac.customerName
                                    : ""
                    }`;

                groups[name] = groups[name] ? [...groups[name], ac] : [ac];
                return groups;
            }, {} as { [key: string]: AccountsReceivableReportItemDto[] })
        );
    }

    pagingParams = new PagingParams();
    pagination: Pagination | null = null;
    predicate = new Map().set("all", true);

    get axiosParams() {
        const params = new URLSearchParams();
        this.predicate.forEach((value, key) => {
            if (key === "startDate" || key === "endDate") {
                params.append(key, (value as Date).toISOString());
            } else {
                params.append(key, value);
            }
        });
        return params;
    }

    setPredicate = (
        startDate?: Date,
        endDate?: Date,
        dateRange?: DateRangeOption,
        type?: ReportType,
        typeOfBalance?: ARReportFilter
      ) => {
        const resetPredicate = () => {
          this.predicate.forEach((value, key) => {
            if (key !== "startDate") this.predicate.delete(key);
          });
        };
        resetPredicate();
        if (startDate) this.predicate.set("startDate", startDate);
        if (endDate) this.predicate.set("endDate", endDate);
        if (dateRange) this.predicate.set('dateRange', dateRange);
        if (type) this.predicate.set("type", type);
        if (typeOfBalance) this.predicate.set("typeOfBalance", typeOfBalance);
    };

    
    setReportCriteria = (values: ReportInput) => {
        this.reportCriteria = new ReportInput(values);
    };

    calculateTotals = (data: AccountsReceivableReportItemDto[]) => {
        const totalQuantity = data.reduce(
            (accumulator: number, item: AccountsReceivableReportItemDto) => {
                accumulator += item.total;
                return accumulator;
            }, 0);

        return totalQuantity;
    };

    calculateTotalBalanceDue = (data: AccountsReceivableReportItemDto[]) => {
        const totalCoreValues = data.reduce(
            (accumulator: number, item: AccountsReceivableReportItemDto) => {
                accumulator += item.balanceDue;
                return accumulator;
            }, 0);


        return totalCoreValues;
    };
}
